<template>
  <div class="tasks-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ isMine ? t('tasks.myTitle') : t('tasks.teamTitle') }}</h1>
        <p class="page-sub">{{ isMine ? t('tasks.mySub') : t('tasks.teamSub') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-ghost" :disabled="!tasks.length" @click="exportTasks">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {{ t('common.export') }}
        </button>
        <button v-if="!isExternal" class="btn btn-primary" @click="openCreateForm">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ t('tasks.newTask') }}
        </button>
      </div>
    </div>

    <!-- Filters bar -->
    <div class="filters">
      <div class="status-tabs">
        <button
          v-for="s in statusOpts"
          :key="s.value"
          class="tab-btn"
          :class="{ active: filters.status === s.value }"
          @click="filters.status = s.value; load()"
        >{{ s.label }}</button>
      </div>
      <div class="filter-controls">
        <select v-model="filters.urgency" class="form-control filter-sel" @change="load()">
          <option value="">{{ t('tasks.allUrgency') }}</option>
          <option value="critical">{{ t('urgency.critical') }}</option>
          <option value="high">{{ t('urgency.high') }}</option>
          <option value="normal">{{ t('urgency.normal') }}</option>
          <option value="low">{{ t('urgency.low') }}</option>
        </select>
        <div class="date-filter">
          <input v-model="filters.due_from" type="date" class="form-control filter-date" :title="t('common.from')" @change="load()" />
          <span class="date-sep">–</span>
          <input v-model="filters.due_to" type="date" class="form-control filter-date" :title="t('common.to')" @change="load()" />
        </div>
        <input
          v-model="filters.q"
          class="form-control filter-search"
          :placeholder="t('common.search')"
          @keyup.enter="load()"
        />
        <button class="btn btn-ghost btn-sm" @click="resetFilters">{{ t('common.reset') }}</button>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="tasks.length === 0" class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
      <p>{{ t('tasks.empty') }}</p>
    </div>

    <!-- Tasks grouped under their project as a table (ungrouped tasks last). -->
    <div v-else class="task-groups">
      <section
        v-for="g in groupedTasks"
        :key="g.key"
        class="proj-table"
        :style="{ '--g-color': g.color || 'var(--border-2)', '--tt-cols': colsFor(g) }"
      >
        <!-- Project box header -->
        <div class="pt-head">
          <span class="pt-dot" :style="{ background: g.color || 'var(--border-2)' }"></span>
          <h3 class="pt-name">{{ g.name }}</h3>
          <span class="pt-count">{{ g.tasks.length }}</span>
          <button v-if="!isExternal && g.key" class="pt-add" @click="openCreateInProject(g.key)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ t('tasks.newTask') }}
          </button>
          <button v-if="!isExternal && g.key" class="pt-add pt-field" @click="openFields(g)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ t('fields.addField') }}
          </button>
        </div>

        <!-- Column headers -->
        <div class="pt-cols">
          <span class="ptc">{{ t('teamReports.subject') }}</span>
          <span class="ptc">{{ t('teamReports.timeCol') }}</span>
          <span class="ptc">{{ t('taskForm.urgency') }}</span>
          <span class="ptc">{{ t('export.assignee') }}</span>
          <span
            v-for="(f, i) in g.fields"
            :key="f.id"
            class="ptc ptc-field"
            draggable="true"
            @dragstart="dragStart(g, i)"
            @dragover.prevent
            @drop="dropOn(g, i)"
          >{{ f.label }}</span>
          <span class="ptc"></span>
        </div>

        <TaskCard
          v-for="task in g.tasks"
          :key="task.id"
          :task="task"
          :users="users"
          :fields="g.fields"
          table
          @open="openTask"
          @complete="completeTask"
          @reopen="reopenTask"
          @restore="restoreTask"
          @assign="assignTask"
          @timer-update="onTimerUpdate"
          @field-value="onFieldValue"
        />
      </section>
    </div>

    <!-- Detail/edit side drawer (opened by clicking a task) -->
    <TaskDetailDrawer
      v-if="drawerTask"
      :task="drawerTask"
      :projects="projects"
      @close="drawerTask = null"
      @saved="onDrawerSaved"
      @changed="load(true)"
      @complete="completeTask"
      @reopen="reopenTask"
      @restore="restoreTask"
    />

    <!-- Create-only modal (editing happens in the drawer) -->
    <TaskFormModal
      v-if="showForm"
      :projects="projects"
      :defaults="createDefaults"
      @close="closeForm"
      @saved="onSaved"
    />

    <FieldManagerModal
      v-if="fieldModalProject"
      :project-id="fieldModalProject.id"
      :project-name="fieldModalProject.name"
      :fields="fieldsFor(fieldModalProject.id)"
      @close="fieldModalProject = null"
      @changed="loadFields"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import TaskCard      from '@/components/TaskCard.vue'
import TaskFormModal from '@/components/TaskFormModal.vue'
import TaskDetailDrawer from '@/components/TaskDetailDrawer.vue'
import FieldManagerModal from '@/components/FieldManagerModal.vue'
import { useI18n } from '@/composables/useI18n'
import { useAuth } from '@/composables/useAuth'
import { useToast } from '@/composables/useToast'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { exportCsv, secondsToHours } from '@/utils/exportCsv'
import api from '@/api'

export default {
  name: 'TasksView',
  components: { TaskCard, TaskFormModal, TaskDetailDrawer, FieldManagerModal },
  setup() {
    const route   = useRoute()
    const { t }   = useI18n()
    const toast   = useToast()
    const { isExternalClient } = useAuth()
    const isExternal = computed(() => isExternalClient())
    // "All tasks" includes completed/archived work (so it can be reviewed and
    // restored); the default board shows only active (open/in-progress) tasks.
    const statusOpts = computed(() => [
      { value: '',            label: t('common.all') },
      { value: 'open',        label: t('status.open') },
      { value: 'in_progress', label: t('status.in_progress') },
      { value: 'all',         label: t('tasks.allTasks') },
    ])
    const tasks    = ref([])
    const projects = ref([])
    const users    = ref([])
    const allFields  = ref([])   // every field the user can see (project + global)
    const fieldModalProject = ref(null)  // { id, name } when the modal is open

    async function loadFields() {
      if (isExternal.value) return
      try { allFields.value = (await api.getProjectFields()).data || [] } catch {}
    }
    // Fields visible for a given project id: its own + global, in sort order.
    function fieldsFor(projectId) {
      return allFields.value
        .filter(f => f.project_id === null || f.project_id === projectId)
        .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
    }
    const loading  = ref(true)
    const showForm = ref(false)
    const drawerTask = ref(null)

    const isMine = computed(() => !!route.query.mine)

    // Group tasks under their project; tasks with no project fall into a final group.
    const groupedTasks = computed(() => {
      const groups = new Map()
      for (const tk of tasks.value) {
        const key = tk.project_id || 0
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            name: tk.project_name || t('tasks.noProjectGroup'),
            color: tk.project_color,
            order: tk.project_id ? 0 : 1,
            tasks: [],
            fields: key ? fieldsFor(key) : [],
          })
        }
        groups.get(key).tasks.push(tk)
      }
      return [...groups.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
    })

    const filters = reactive({ status: '', urgency: '', project_id: route.query.project_id || '', q: '', due_from: '', due_to: '' })

    // silent=true skips the loading spinner so a realtime refresh doesn't unmount
    // the task rows (which would collapse any open subtask/comment panels).
    async function load(silent = false) {
      if (!silent) loading.value = true
      try {
        const params = {
          ...(isMine.value         ? { mine: 1 }                           : {}),
          // "all" widens the scope to include archived/completed tasks; the
          // open/in_progress tabs filter by status on the active set.
          ...(filters.status === 'all'
            ? { scope: 'all' }
            : (filters.status ? { status: filters.status } : {})),
          ...(filters.urgency      ? { urgency: filters.urgency }          : {}),
          ...(filters.project_id   ? { project_id: filters.project_id }    : {}),
          ...(filters.q.trim()     ? { q: filters.q.trim() }               : {}),
          ...(filters.due_from     ? { due_from: filters.due_from }        : {}),
          ...(filters.due_to       ? { due_to: filters.due_to }            : {}),
        }
        const res    = await api.getTasks(params)
        tasks.value  = res.data || []
      } catch {} finally {
        loading.value = false
      }
    }

    async function loadProjects() {
      try { projects.value = (await api.getProjects()).data || [] } catch {}
    }
    async function loadUsers() {
      if (isExternal.value) return
      try { users.value = (await api.getUsers()).data || [] } catch {}
    }

    // Inline quick-assign from a task card — no drawer needed.
    async function assignTask({ task, userId }) {
      const prev = task.assigned_to
      const name = users.value.find(u => u.id === userId)?.name || null
      task.assigned_to = userId
      task.assignee_name = name
      try {
        await api.updateTask(task.id, { assigned_to: userId })
        toast.success(userId ? t('toast.assigned', { name }) : t('toast.unassigned'))
      } catch {
        task.assigned_to = prev
        await load(true)
        toast.error(t('toast.actionFailed'))
      }
    }

    function resetFilters() {
      Object.assign(filters, { status: '', urgency: '', project_id: '', q: '', due_from: '', due_to: '' })
      load()
    }

    // Completing a task archives it (it leaves the active board and is reachable
    // again via the "All tasks" filter).
    async function completeTask(task) {
      try { await api.archiveTask(task.id); drawerTask.value = null; await load(); toast.success(t('toast.taskCompleted')) }
      catch { toast.error(t('toast.actionFailed')) }
    }
    async function reopenTask(task) {
      try { await api.reopenTask(task.id); await load(); toast.success(t('toast.taskReopened')) }
      catch { toast.error(t('toast.actionFailed')) }
    }
    async function restoreTask(task) {
      try { await api.restoreTask(task.id); drawerTask.value = null; await load(); toast.success(t('toast.taskRestored')) }
      catch { toast.error(t('toast.actionFailed')) }
    }
    function openTask(task) { drawerTask.value = task }
    function onDrawerSaved() { drawerTask.value = null; load() }
    const createDefaults = ref({})
    function openCreateForm() { createDefaults.value = {}; showForm.value = true }
    function openCreateInProject(pid) { createDefaults.value = { project_id: pid }; showForm.value = true }
    function closeForm() { showForm.value = false; createDefaults.value = {} }
    function onSaved() { load() }

    // Grid template per group: base columns + one 130px track per custom field,
    // inserted before the trailing action column.
    function colsFor(group) {
      const fieldCols = (group.fields || []).map(() => '130px').join(' ')
      return `1fr 150px 116px 64px${fieldCols ? ' ' + fieldCols : ''} 138px`
    }

    function exportTasks() {
      const rows = tasks.value.map(tk => ({
        [t('export.task')]:     tk.title,
        [t('export.project')]:  tk.project_name || '',
        [t('export.assignee')]: tk.assignee_name || '',
        [t('export.status')]:   t('status.' + tk.status),
        [t('export.urgency')]:  t('urgency.' + tk.urgency),
        [t('export.hours')]:    secondsToHours(tk.total_seconds),
        [t('export.due')]:      tk.due_at ? tk.due_at.slice(0, 10) : '',
      }))
      const stamp = new Date().toISOString().slice(0, 10)
      exportCsv(rows, `devtask-tasks-${stamp}.csv`)
    }

    function onTimerUpdate(payload) {
      const t = tasks.value.find(t => t.id === payload.taskId)
      if (t) {
        t.timer_seconds = payload.timer_seconds
        t.timer_started_at = payload.timer_started_at
        if (payload.status) t.status = payload.status
      }
    }

    function openFields(group) { fieldModalProject.value = { id: group.key, name: group.name } }

    // Optimistic inline value save.
    async function onFieldValue({ task, fieldId, value }) {
      const prev = { ...(task.field_values || {}) }
      const next = { ...(task.field_values || {}) }
      if (value === '' || value == null) delete next[fieldId]; else next[fieldId] = value
      task.field_values = next
      try { await api.updateTask(task.id, { field_values: { [fieldId]: value } }) }
      catch { task.field_values = prev; toast.error(t('toast.saveFailed')) }
    }

    // Drag-reorder field columns within a group (custom fields only).
    const drag = reactive({ projectId: null, from: -1 })
    function dragStart(group, i) { drag.projectId = group.key; drag.from = i }
    async function dropOn(group, i) {
      if (drag.projectId !== group.key || drag.from < 0 || drag.from === i) return
      const ids = group.fields.map(f => f.id)
      const [moved] = ids.splice(drag.from, 1)
      ids.splice(i, 0, moved)
      drag.from = -1
      try { await api.reorderProjectFields(group.key, ids); await loadFields() }
      catch { toast.error(t('toast.actionFailed')) }
    }

    // React to navigation between project/mine views (e.g. clicking "View tasks").
    watch(() => [route.query.mine, route.query.project_id], () => {
      filters.project_id = route.query.project_id || ''
      load()
    })
    let offRealtime = () => {}
    let offFields = () => {}
    onMounted(() => {
      load(); loadProjects(); loadUsers(); loadFields()
      offRealtime = onRealtime('task.changed', debounce(() => load(true), 400))
      offFields = onRealtime('project.changed', debounce(loadFields, 400))
    })
    onUnmounted(() => { offRealtime(); offFields() })

    return {
      statusOpts, tasks, groupedTasks, projects, users, loading, showForm, drawerTask, createDefaults,
      isMine, isExternal, filters, t,
      load, resetFilters, completeTask, reopenTask, restoreTask, assignTask,
      openTask, onDrawerSaved, openCreateForm, openCreateInProject, closeForm, onSaved, onTimerUpdate, exportTasks,
      colsFor, allFields, fieldsFor, fieldModalProject, openFields, onFieldValue, dragStart, dropOn,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.header-actions { display: flex; gap: 8px; align-items: center; }

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 14px 16px;
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: var(--radius);
}
.status-tabs { display: flex; gap: 4px; }
.tab-btn {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: none;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
}
.tab-btn:hover  { border-color: var(--primary); color: var(--primary); }
.tab-btn.active { background: var(--primary); border-color: var(--primary); color: #fff; }

.filter-controls { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
.filter-sel    { width: 140px; padding: 6px 10px; font-size: 12px; }
.filter-search { width: 180px; padding: 6px 10px; font-size: 12px; }
.date-filter   { display: flex; align-items: center; gap: 6px; }
.filter-date   { width: 140px; padding: 6px 10px; font-size: 12px; }
.date-sep      { color: var(--muted); }

.tasks-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Project groups rendered as table cards. */
.task-groups { display: flex; flex-direction: column; gap: 22px; }
.proj-table {
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-inline-start: 4px solid var(--g-color, var(--border-2));
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(20,24,40,.04);
}

/* Project box header */
.pt-head { display: flex; align-items: center; gap: 11px; padding: 14px 18px; }
.pt-dot { width: 11px; height: 11px; border-radius: 4px; flex-shrink: 0; }
.pt-name { font-size: var(--fs-h4); font-weight: 800; letter-spacing: -.01em; }
.pt-count {
  min-width: 22px; height: 22px; padding: 0 6px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--danger); color: #fff;
  border-radius: 999px; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums;
}
.pt-add {
  margin-inline-start: auto;
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--accent); color: var(--on-accent);
  border: none; border-radius: 20px; padding: 7px 14px;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.pt-add:hover { filter: brightness(1.05); }
.pt-field { background: var(--surface-2); color: var(--accent-ink); border: 1px dashed color-mix(in srgb, var(--accent-ink) 40%, transparent); }
.pt-field:hover { filter: none; background: var(--accent-wash); }
.ptc-field { cursor: grab; user-select: none; }
.ptc-field:active { cursor: grabbing; }

/* Column headers — aligned to the same grid as the rows. */
.pt-cols {
  display: grid; grid-template-columns: var(--tt-cols);
  background: var(--surface-2);
  border-block: 1px solid var(--border);
}
.ptc {
  padding: 8px 14px; font-size: 11px; font-weight: 600;
  color: var(--muted); text-transform: uppercase; letter-spacing: .03em; white-space: nowrap;
}
/* The table rows are TaskCards; drop the trailing divider on the last one. */
.proj-table > .task-card-wrap:last-child :deep(.task-row.tbl) { border-bottom: none; }

@media (max-width: 760px) {
  .pt-cols { display: none; }
}

@media (max-width: 700px) {
  .filter-controls { flex-direction: column; }
  .filter-sel, .filter-search { width: 100%; }
  .tasks-grid { grid-template-columns: 1fr; }
}
</style>
